<template>
  <!-- eslint-disable max-len -->
  <div class="content overlay__inner" :class="formClass">
    <div class="heading-bar">
      <div class="heading-bar__actions">
        <button type="button" class="close-icon" @click="close">&times;</button>
      </div>
      <h1 v-if="!editMode" class="hdg hdg--3">Add availability</h1>
      <h1 v-else class="hdg hdg--3">Edit availability</h1>
    </div>

    <form @submit.prevent="submitForm">
      <div class="form__section">
        <div class="form__group">
          <div class="form__group-item">
            <poppins-date-picker
              id="form-date"
              v-model="sittingDate"
              labelName="Available date"
              :maxDate="maxStartDate"
              :status="sittingDateStatus"
              :helperText="sittingDateHelperText"
              :customClasses="'w-full'"
              @manual-input="validateTypedInput"
            />
            <p v-if="errors['sittingDate']" class="form__error">{{ errors['sittingDate'] }}</p>
          </div>
        </div>

        <div class="form__group">
          <div class="form__group-item form__group-item--col form__group-item--mb-3">
            <poppins-time-picker
              id="form-start-date"
              v-model="startTime"
              labelName="Start time"
              :start="startRange[0]"
              :end="startRange[1]"
              :status="startTimeStatus"
              :helperText="startTimeHelperText"
            />
          </div>

          <div class="form__group-item form__group-item--col form__group-item--mb-3">
            <poppins-time-picker
              id="regular-schedule-end"
              v-model="endTime"
              labelName="End time"
              :start="endRange[0]"
              :end="endRange[1]"
              :status="endTimeStatus"
              :helperText="endTimeHelperText"
            />
          </div>
        </div>
      </div>

      <div v-if="!editMode" class="form__repeat-group">
        <poppins-checkbox
          v-model="repeating"
          value="repeat"
          checkbox-id="repeat"
          labelName="Repeat this time block"
          labelClass="mb-0"
        />
      </div>

      <div v-if="!editMode && repeating" class="form__repeating">
        <div class="form__repeat-group">
          <poppins-radio-button
            id="repeat_on_weekly"
            v-model="repeatOn"
            radioName="repeat_on"
            :labelName="`Weekly on ${repeatDay}`"
            radioValue="weekly"
          />
        </div>

        <div class="form__repeat-group">
          <poppins-radio-button
            id="repeat_on_daily"
            v-model="repeatOn"
            :disabled="!showEveryWeekday"
            radioName="repeat_on"
            labelName="Every weekday (Mon to Fri)"
            radioValue="weekdays"
          />
          <p v-if="errors['repetition']" class="form__error">{{ errors['repetition'] }}</p>
        </div>

        <div class="form__repeat-group">
          <p class="form__notice">This will be repeated for about 90 days.</p>
        </div>
      </div>

      <div class="form__section form__section--flex-buttons">
        <Button v-if="editMode" value="Delete" variant="outlined" size="medium" class="w-full" @click.prevent="clear" />
        <Button value="Save" variant="primary" size="medium" class="w-full" />
      </div>
    </form>
  </div>
  <!-- eslint-enable max-len -->
</template>

<script lang="ts">
import { addDays, addMinutes, endOfDay, format, isBefore, isValid, parseISO, set, startOfDay } from 'date-fns';
import helperStatus from '@/helpers/status';
import PoppinsDatePicker from '@/components/PoppinsDatePicker/PoppinsDatePicker.vue';
import PoppinsTimePicker from '@/components/timepicker/PoppinsTimePicker.vue';
import Button from '@/components/button/Button.vue';
import Checkbox from '@/components/checkbox/Checkbox.vue';
import RadioButton from '@/components/radio_button/RadioButton.vue';

export default {
  name: 'AvailabilityForm',
  components: {
    PoppinsDatePicker,
    PoppinsTimePicker,
    Button,
    PoppinsCheckbox: Checkbox,
    PoppinsRadioButton: RadioButton,
  },
  props: {
    availability: {
      type: Object,
      default: null,
    },
    formClass: {
      type: String,
      default: '',
    },
  },
  data(): {
    errors: any;
    sittingDate: Date | null;
    startTime: Date | null;
    endTime: Date | null;
    repeating: boolean;
    repeatOn: string;
    sittingDateHelperText: string;
    endTimeHelperText: string;
    startTimeHelperText: string;
  } {
    return {
      errors: {},
      sittingDate: null,
      startTime: null,
      endTime: null,
      repeating: false,
      repeatOn: 'weekly',
      sittingDateHelperText: '',
      endTimeHelperText: '',
      startTimeHelperText: '',
    };
  },
  computed: {
    maxStartDate(): Date {
      const today = new Date();
      const thirtyDaysFromToday = new Date();
      // returning the code below will return a number, don't do it, you need to return a date
      thirtyDaysFromToday.setDate(today.getDate() + 30);

      // returning the variable like this will return a date
      return thirtyDaysFromToday;
    },
    editMode(): boolean {
      return !!this.availability;
    },
    startRange(): Array<Date> {
      if (!this.sittingDate) return [];

      const s = startOfDay(this.sittingDate);
      const e = endOfDay(this.sittingDate);
      return [s, e];
    },
    endRange(): Array<Date> {
      if (!this.startTime) return [];

      const s = addMinutes(this.startTime, 30);
      const e = addDays(this.startTime, 1);
      return [s, e];
    },
    sittingDateStatus(): helperStatus {
      if (this.sittingDateHelperText) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    startTimeStatus(): helperStatus {
      if (this.startTimeHelperText) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    endTimeStatus(): helperStatus {
      if (this.endTimeHelperText) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    repeatDay(): string {
      if (!this.sittingDate) return '';

      return format(this.sittingDate, 'eee');
    },
    showEveryWeekday(): boolean {
      if (!this.sittingDate) return false;

      const d = this.sittingDate.getDay();
      return d > 0 && d < 6;
    },
  },
  watch: {
    availability() {
      this.sittingDate = this.defaultSittingDate();
      this.startTime = this.defaultStartTime();
      this.endTime = this.defaultEndTime();
      this.errors = {};
    },
    sittingDate(value: Date | null) {
      this.sittingDateHelperText = '';

      if (value === null) {
        this.startTime = null;
        this.endTime = null;
      } else {
        const startDate = set(this.startTime || new Date(), {
          year: value.getFullYear(),
          month: value.getMonth(),
          date: value.getDate(),
        });

        let endDate = set(this.endTime || new Date(), {
          year: value.getFullYear(),
          month: value.getMonth(),
          date: value.getDate(),
        });

        if (isBefore(endDate, startDate)) {
          endDate = addDays(endDate, 1);
        }

        if (isValid(startDate) && isValid(endDate)) {
          this.startTime = startDate;
          this.endTime = endDate;
        }
      }
    },
    startTime() {
      this.startTimeHelperText = '';
    },
    endTime() {
      this.endTimeHelperText = '';
    },
  },
  created(): any {
    this.sittingDate = this.defaultSittingDate();
    this.startTime = this.defaultStartTime();
    this.endTime = this.defaultEndTime();
  },
  methods: {
    submitForm() {
      if (!this.validate()) return;

      if (this.availability) {
        this.$emit('update', this.availability, this.startTime, this.endTime);
      } else if (this.repeating) {
        this.$emit('submitRepeating', this.startTime, this.endTime, this.repeatOn.toUpperCase());
      } else {
        this.$emit('submit', this.startTime, this.endTime);
      }
    },
    clear() {
      this.$emit('clear', this.availability);
    },
    // TODO: There will definitely be a better way of doing validation.
    // TODO: We will need to check with product about the correct messages.
    validate(): boolean {
      const e: any = {};

      if (!this.sittingDate) this.sittingDateHelperText = 'This field is required';
      if (!isValid(this.startTime)) this.startTimeHelperText = 'This field is required';
      if (!isValid(this.endTime)) this.endTimeHelperText = 'This field is required';

      if (this.endTime && this.startTime && isBefore(this.endTime, this.startTime)) {
        this.endTimeHelperText = 'Choose an end time later than the start time.';
      }

      if (!this.showEveryWeekday && this.repeatOn === 'weekdays') e.repetition = 'Weekday repetition cannot be used on a weekend';

      this.errors = e;

      if (this.startTimeHelperText || this.endTimeHelperText) return false;
      if (Object.keys(e).length !== 0) return false;

      return true;
    },
    validateTypedInput(dateString): boolean {
      const e: any = {};

      const date = new Date(dateString);
      const today = new Date();

      if (date < today || date > this.maxStartDate) {
        e.sittingDate = 'Please enter a date within a month of today.';
        this.sittingDate = null;
      } else {
        this.sittingDate = date;
      }

      this.errors = e;

      return Object.keys(e).length === 0;
    },
    close() {
      this.$emit('close');
    },
    defaultSittingDate(): Date {
      if (this.availability) {
        return startOfDay(parseISO(this.availability.node.start));
      }
      return addDays(new Date(), 2);
    },
    defaultStartTime(): Date | null {
      if (this.availability) {
        return parseISO(this.availability.node.start);
      }
      return null;
    },
    defaultEndTime(): Date | null {
      if (this.availability) {
        return parseISO(this.availability.node.end);
      }
      return null;
    },
  },
};
</script>
